import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentQuotationTask } from '@/providers/QuotationTaskProvider';
import { useQuotationTaskDocuments, useQuotationTaskDocumentsInvalidate } from '@/hooks/api/1540_quotation_task_documents';
import { useDestroyQuotationTaskDocument } from '@/hooks/api/1570_destroy_quotation_task_document';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { QuotationTaskDocument } from '@/hooks/api/models';
import { toastApiError } from '@/lib/toast';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { useUpdateQuotationTaskDocument } from '@/hooks/api/1560_update_quotation_task_document';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { useQuotationTaskInvalidate } from '@/hooks/api/1480_quotation_task';

export const DocumentsElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { data: documents } = useQuotationTaskDocuments({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    task_id: task!.id,
  });
  const { mutate, isPending, isSuccess, error } = useDestroyQuotationTaskDocument();
  const { invalidate } = useQuotationTaskDocumentsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    task_id: task!.id,
  });
  const { invalidate: invalidateTask } = useQuotationTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id!),
    id: task!.id,
  });
  const [delTarget, setDelTarget] = useState<QuotationTaskDocument>();

  useEffect(() => {
    if (isSuccess) {
      invalidate();
      invalidateTask();
    }
  }, [isSuccess]);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onDeleteClick = () => {
    setShowLoading(true);
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      id: delTarget!.id,
    });
  };

  if (documents == null || documents.length == 0) return <></>;

  return (
    <div className="table-responsive">
      <ModalConfirm modalId={`delConfirm`} okClicked={() => onDeleteClick()} message={'削除します。よろしいですか？'} />

      <table className="table align-items-center">
        <thead>
          <tr>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">ファイル</th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">URL</th>
            <th className="text-secondary opacity-7" style={{ width: 100 }}></th>
          </tr>
        </thead>
        <tbody>
          {documents.map((d) => (
            <DocumentRow key={`qtd-${d.id}`} d={d} setDelTarget={setDelTarget} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  task_id: number;
  id: number;
  name: string;
};

const DocumentRow = ({ d, setDelTarget }: { d: QuotationTaskDocument; setDelTarget: Dispatch<SetStateAction<QuotationTaskDocument | undefined>> }) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, error, isPending } = useUpdateQuotationTaskDocument();
  const { invalidate } = useQuotationTaskDocumentsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    task_id: task!.id,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      id: d.id,
      name: d.name,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<FormInputs> = (inputData) => {
    setShowLoading(true);
    mutate(inputData);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      setEditing(false);
    }
  }, [data]);

  if (!editing) {
    return (
      <tr>
        <td className="h6">{d.name}</td>
        <td className="text-secondary text-sm" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {d.url && (
            <Link to={d.url} target={'_blank'}>
              {d.url}
            </Link>
          )}
          {d.file_url && (
            <Link to={d.file_url} target={'_blank'} style={{}}>
              {d.file_url}
            </Link>
          )}
        </td>
        <td>
          <button className="btn btn-icon-only bg-gradient-success me-1" onClick={() => setEditing((e) => !e)}>
            <i className="fas fa-edit"></i>
          </button>
          <button
            className={`btn btn-icon-only bg-gradient-danger`}
            data-bs-toggle="modal"
            data-bs-target={`#delConfirm`}
            onClick={() => setDelTarget((_) => d)}
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="h6">
        <input type="text" maxLength={50} className={`form-control ${errors.name && 'is-invalid'}`} {...register('name', { required: '入力して下さい。' })} />
      </td>
      <td className="text-secondary text-sm" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {d.url && (
          <Link to={d.url} target={'_blank'}>
            {d.url}
          </Link>
        )}
        {d.file_url && (
          <Link to={d.file_url} target={'_blank'} style={{}}>
            {d.file_url}
          </Link>
        )}
      </td>
      <td>
        <button className="btn btn-icon-only btn-outline-secondary me-1" onClick={() => setEditing((e) => !e)}>
          <i className="fas fa-undo"></i>
        </button>
        <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
          <i className="fas fa-check-circle"></i>
        </button>
      </td>
    </tr>
  );
};
